/** Cryptographic verification of a parsed timestamp token.
 *
 * Three independent checks (FR-13):
 *   1. CMS signature over TSTInfo (incl. signed attributes)
 *   2. Certificate chain to a trusted root — evaluated AT genTime (FR-9)
 *   3. Original document digest matches messageImprint (skip if no file)
 */

import * as pkijs from "pkijs";

import { toArrayBuffer, toHex } from "./bytes";
import { HASH_OID_TO_WEBCRYPTO } from "./oids";
import type { CheckState, ParsedTsr } from "./parse";

export interface SignatureVerdict {
  state: CheckState;
  detail: string;
}

export interface ChainVerdict {
  state: CheckState;
  detail: string;
  trustedRootSubject?: string;
  /** true when the signer cert is expired *now* but was valid at genTime */
  expiredNowValidAtGenTime?: boolean;
}

export interface DocumentVerdict {
  state: CheckState;
  detail: string;
  computedHex?: string;
}

export interface VerificationResult {
  signature: SignatureVerdict;
  chain: ChainVerdict;
  document: DocumentVerdict;
}

export async function verifySignature(
  parsed: ParsedTsr
): Promise<SignatureVerdict> {
  const { signedData } = parsed;
  if (!signedData || !parsed.tstInfo) {
    return { state: "skip", detail: "No timestamp token to verify." };
  }
  if (parsed.signerCertIndex === null) {
    return {
      state: "skip",
      detail:
        "The token does not embed the signer certificate (certReq was false); " +
        "the signature cannot be checked without it.",
    };
  }
  // Verified manually rather than via pkijs SignedData.verify, whose TSTInfo
  // branch insists on the original document — our document check is separate.
  try {
    const signer = signedData.signerInfos[0];
    const certs = (signedData.certificates ?? []).filter(
      (c): c is pkijs.Certificate => c instanceof pkijs.Certificate
    );
    const cert = certs[parsed.signerCertIndex];
    const crypto = pkijs.getCrypto(true);

    if (!signer.signedAttrs) {
      return {
        state: "fail",
        detail:
          "The token has no signed attributes — RFC 3161 requires them for " +
          "timestamp tokens.",
      };
    }

    const digestName =
      HASH_OID_TO_WEBCRYPTO[signer.digestAlgorithm.algorithmId];
    if (!digestName) {
      return {
        state: "fail",
        detail: `Unsupported digest algorithm ${signer.digestAlgorithm.algorithmId}.`,
      };
    }

    // 1) messageDigest signed attribute must equal digest(TSTInfo DER).
    const mdAttr = signer.signedAttrs.attributes.find(
      (a) => a.type === "1.2.840.113549.1.9.4"
    );
    const mdValue = mdAttr?.values[0]?.valueBlock?.valueHexView;
    if (!mdValue || !parsed.tstBytes) {
      return {
        state: "fail",
        detail: "The token is missing the messageDigest signed attribute.",
      };
    }
    const computed = new Uint8Array(
      await crypto.digest({ name: digestName }, toArrayBuffer(parsed.tstBytes))
    );
    if (toHex(computed) !== toHex(new Uint8Array(mdValue))) {
      return {
        state: "fail",
        detail:
          "The messageDigest signed attribute does not match the TSTInfo — " +
          "the token content has been altered after signing.",
      };
    }

    // 2) The signature covers the SET OF encoding of the signed attributes.
    const ok = await crypto.verifyWithPublicKey(
      signer.signedAttrs.encodedValue,
      signer.signature,
      cert.subjectPublicKeyInfo,
      signer.signatureAlgorithm,
      digestName
    );
    return ok
      ? {
          state: "pass",
          detail:
            "The CMS signature over the TSTInfo (including signed attributes) " +
            "verifies against the embedded signer certificate.",
        }
      : { state: "fail", detail: "The CMS signature does not verify." };
  } catch (err) {
    return {
      state: "fail",
      detail: `The CMS signature does not verify: ${errText(err)}`,
    };
  }
}

export async function verifyChain(
  parsed: ParsedTsr,
  trustedRoots: pkijs.Certificate[]
): Promise<ChainVerdict> {
  if (!parsed.signedData || !parsed.tstInfo) {
    return { state: "skip", detail: "No timestamp token to verify." };
  }
  const certs = (parsed.signedData.certificates ?? []).filter(
    (c): c is pkijs.Certificate => c instanceof pkijs.Certificate
  );
  if (parsed.signerCertIndex === null || certs.length === 0) {
    return {
      state: "skip",
      detail: "No embedded certificates — supply the TSA chain to validate.",
    };
  }
  if (trustedRoots.length === 0) {
    return { state: "skip", detail: "Trust store is empty." };
  }

  const genTime = new Date(parsed.tstInfo.genTime);
  const leaf = certs[parsed.signerCertIndex];

  // Hard fail if the signer cert was not valid AT GENTIME (FR-9).
  if (leaf.notBefore.value > genTime || leaf.notAfter.value < genTime) {
    return {
      state: "fail",
      detail:
        `The signer certificate was not valid at genTime ` +
        `(${parsed.tstInfo.genTime}): its validity window is ` +
        `${leaf.notBefore.value.toISOString()} – ${leaf.notAfter.value.toISOString()}.`,
    };
  }

  // EKU: RFC 3161 §2.3 — exactly id-kp-timeStamping, critical.
  const leafSummary = parsed.certs[parsed.signerCertIndex];
  if (!leafSummary.isTimeStampingEku || !leafSummary.ekuCritical) {
    return {
      state: "fail",
      detail:
        "The signer certificate lacks a critical id-kp-timeStamping extended " +
        "key usage, which RFC 3161 requires for TSA certificates.",
    };
  }

  try {
    const engine = new pkijs.CertificateChainValidationEngine({
      certs,
      trustedCerts: trustedRoots,
      checkDate: genTime,
    });
    const result = await engine.verify();
    if (!result.result) {
      return {
        state: "fail",
        detail: `Chain validation failed: ${result.resultMessage || "no path to a trusted root"}.`,
      };
    }
    const path = result.certificatePath ?? [];
    const root = path[path.length - 1];
    const rootSubject = root
      ? subjectCn(root)
      : matchedRootSubject(certs, trustedRoots);
    const expiredNow = leaf.notAfter.value < new Date();
    return {
      state: "pass",
      detail:
        `The signer chains to trusted root “${rootSubject}”. Validity was ` +
        `evaluated at genTime, as RFC 3161 intends.` +
        (expiredNow
          ? " Note: the signer certificate has since expired — that does not " +
            "invalidate this timestamp."
          : ""),
      trustedRootSubject: rootSubject,
      expiredNowValidAtGenTime: expiredNow,
    };
  } catch (err) {
    return { state: "fail", detail: `Chain validation failed: ${errText(err)}.` };
  }
}

export async function verifyDocumentDigest(
  parsed: ParsedTsr,
  documentDigestHex: string | null
): Promise<DocumentVerdict> {
  if (!parsed.tstInfo) {
    return { state: "skip", detail: "No timestamp token." };
  }
  if (documentDigestHex === null) {
    return {
      state: "skip",
      detail:
        "No original file supplied — drop the timestamped file to prove it " +
        "matches this token.",
    };
  }
  const match =
    documentDigestHex.toLowerCase() ===
    parsed.tstInfo.messageImprintHex.toLowerCase();
  return match
    ? {
        state: "pass",
        detail: `The file's ${parsed.tstInfo.hashAlgName} digest matches the token's messageImprint.`,
        computedHex: documentDigestHex,
      }
    : {
        state: "fail",
        detail:
          `The file's ${parsed.tstInfo.hashAlgName} digest does not match the ` +
          "token's messageImprint — this is not the timestamped file (or it " +
          "has been modified).",
        computedHex: documentDigestHex,
      };
}

export function webCryptoAlgoForImprint(parsed: ParsedTsr): string | null {
  if (!parsed.tstInfo) return null;
  return HASH_OID_TO_WEBCRYPTO[parsed.tstInfo.hashAlgOid] ?? null;
}

export async function certSha256Fingerprint(
  cert: pkijs.Certificate
): Promise<string> {
  const der = cert.toSchema().toBER();
  const digest = await pkijs.getCrypto(true).digest({ name: "SHA-256" }, der);
  return toHex(new Uint8Array(digest));
}

function subjectCn(cert: pkijs.Certificate): string {
  const cn = cert.subject.typesAndValues.find((tv) => tv.type === "2.5.4.3");
  return cn ? String(cn.value.valueBlock.value) : "unknown root";
}

function matchedRootSubject(
  certs: pkijs.Certificate[],
  roots: pkijs.Certificate[]
): string {
  for (const cert of certs) {
    for (const root of roots) {
      if (cert.issuer.isEqual(root.subject)) return subjectCn(root);
    }
  }
  return "trusted root";
}

function errText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
